import { HttpClient, HttpErrorResponse, HttpHeaders, HttpRequest } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Observable, of, throwError } from 'rxjs';
import { catchError, map, shareReplay, switchMap, tap } from 'rxjs/operators';
import { Change, changeResult, RevertError } from './change';
import { HubService } from './hub.service';
import { SearchCriteria } from './searchCriteria';
import { getEffectiveOrgId } from '../shared/effective-org-id';

interface CacheEntry<T> {
  data$: Observable<T>;
  timestamp: number;
}

const CACHE_TTL_MS = 5 * 60 * 1000; // 5 minutes

@Injectable({
  providedIn: 'root'
})
export class ChangeService {

  private _Hub = inject(HubService);
  private _http = inject(HttpClient);

  private _changedByCache = new Map<string, CacheEntry<string[]>>();
  private _nxtListCache = new Map<string, CacheEntry<{ id: string, name: string }[]>>();


  //  searchRecords(search: SearchCriteria): Observable<Change[]> {

  searchRecords(search: SearchCriteria): Observable<Change[]> {

    return this._Hub.getOrgStackURL().pipe(switchMap(data => {

      if (data === 'error') {
        return [];
      }


      search.orgId = getEffectiveOrgId();

      const url: string = data.endpoint + 'api/Tracker/search';

      return this._http.post<Change[]>(url, search, { headers: this.getHeaders(data.apikey) })
        .pipe(
          catchError(this.handleError));

    }));

  }

  // older OrgStack instances will not have this method so we should allow for that.
  searchRecordWithCount(search: SearchCriteria): Observable<changeResult> {

    return this._Hub.getOrgStackURL().pipe(switchMap(data => {

      if (data === 'error') {
        return [];
      }


      search.orgId = getEffectiveOrgId();

      const url: string = data.endpoint + 'api/Tracker/searchwithcount';

      return this._http.post<Change[]>(url, search, { headers: this.getHeaders(data.apikey) })
        .pipe(
          catchError(err => of(false)));

    }));

  }



  getVersion(): Observable<string> {

    return this._Hub.getOrgStackURL().pipe(switchMap(data => {

      if (data === 'error') {
        return 'Version unavailable';
      }

      const url: string = data.endpoint + 'api/Values';


      return this._http.get(url, { responseType: 'text', headers: this.getHeaders(data.apikey) })
        .pipe(
          catchError(this.handleError));

    }));

  }


  // The orgId parameter is retained for API compatibility but ignored — the
  // service uses getEffectiveOrgId() so Process Core requests always carry the
  // legacy (pre-ZeidZone-migration) orgId where one exists.
  getSummaryData(_orgId: string): Observable<any> {

    return this._Hub.getOrgStackURL().pipe(switchMap(data => {

      if (data === 'error') {
        return [];
      }

      const url: string = data.endpoint + 'api/Tracker/existing/summary?orgId=' + getEffectiveOrgId();


      return this._http.get(url, { headers: this.getHeaders(data.apikey) })
        .pipe(
          catchError(this.handleError));
    }));

  }



  searchConstituent(_orgId: string, search: string): Observable<{ Id: string, Name: string }[]> {

    return this._Hub.getOrgStackURL().pipe(switchMap(data => {

      if (data === 'error') {
        return [];
      }

      const url: string = data.endpoint + 'api/Tracker/search/constituent/' + getEffectiveOrgId() + '?search=' + search;

      return this._http.get<{ Id: string, Name: string }[]>(url, { headers: this.getHeaders(data.apikey) });

    }));
  }


  getChangedByList(_orgId: string): Observable<string[]> {
    const effectiveOrgId = getEffectiveOrgId();
    return this.getCached(this._changedByCache, effectiveOrgId, () =>
      this._Hub.getOrgStackURL().pipe(switchMap(data => {
        if (data === 'error') {
          return of([] as string[]);
        }
        const url: string = data.endpoint + 'api/Tracker/search/changedByList/' + effectiveOrgId;
        return this._http.get<string[]>(url, { headers: this.getHeaders(data.apikey) })
          .pipe(catchError(() => of([] as string[])));
      }))
    );
  }


  getNXTListofLists(_orgId: string): Observable<{ id: string, name: string }[]> {
    const effectiveOrgId = getEffectiveOrgId();
    return this.getCached(this._nxtListCache, effectiveOrgId, () =>
      this._Hub.getOrgStackURL().pipe(switchMap(data => {
        if (data === 'error') {
          return of([] as { id: string, name: string }[]);
        }
        const url: string = data.endpoint + 'api/Tracker/search/lists/' + effectiveOrgId;
        return this._http.get<{ id: string, name: string }[]>(url, { headers: this.getHeaders(data.apikey) })
          .pipe(catchError(() => of([] as { id: string, name: string }[])));
      }))
    );
  }

  revertChanges(selected: Change[]): Observable<RevertError[]> {

    return this._Hub.getOrgStackURL().pipe(switchMap(data => {

      if (data === 'error') {
        return [];
      }


      let orgId = getEffectiveOrgId();

      const url: string = data.endpoint + 'api/Tracker/revert?orgId=' + orgId;

      return this._http.patch<RevertError[]>(url, selected, { headers: this.getHeaders(data.apikey) })
        .pipe(
          catchError(this.handleError));

    }));

  }



  downloadData(search: SearchCriteria): Observable<any> {


    return this._Hub.getOrgStackURL().pipe(switchMap(data => {

      if (data === 'error') {
        return [];
      }


      search.orgId = getEffectiveOrgId();

      const url: string = data.endpoint + 'api/Tracker/download';

      return this._http.post(url, search, { headers: this.getHeaders(data.apikey), responseType: 'blob' })
        .pipe(
          catchError(err => of(false)));

    }));



  }


  /////////////////////////////////////

  private getCached<T>(cache: Map<string, CacheEntry<T>>, key: string, factory: () => Observable<T>): Observable<T> {
    const entry = cache.get(key);
    if (entry && (Date.now() - entry.timestamp) < CACHE_TTL_MS) {
      return entry.data$;
    }
    const data$ = factory().pipe(shareReplay(1));
    cache.set(key, { data$, timestamp: Date.now() });
    return data$;
  }

  private getHeaders(apikey: string): HttpHeaders {

    let headers = new HttpHeaders();

    headers = headers.set('Content-Type', 'application/json');
    headers = headers.set('x-api-key', apikey);
    // headers = headers.set('Authorization', 'Bearer ' + this._UserService.getAccessToken());
    return headers;
  }

  /////////////////////////////////////

  private handleError(err: HttpErrorResponse) {
    console.log(err.message);
    return throwError(err.message);
  }

  public test(): Observable<string> {

    // https://2dv6f798li.execute-api.ca-central-1.amazonaws.com/Prod/api/Values
    // return this._http.get<string>('https://2dv6f798li.execute-api.ca-central-1.amazonaws.com/Prod/api/Values' );
    return this._http.get<string>('https://2dv6f798li.execute-api.ca-central-1.amazonaws.com/Prod/api/Tracker/existing/phones');

  }

}
